/**
 * Terminal UI with a persistent bottom input region.
 *
 * A side-margined expanding input box stays anchored at the bottom and is always
 * editable — you can type while the agent works. Working status and subagent
 * lines render directly above the box; the permission level sits in the box's
 * top border (far right). All transcript output is printed ABOVE the work area
 * via print(). Approvals and menus temporarily take over the bottom.
 */
import readline from "node:readline";
import { gradientText, brandCycleColor } from "./render.ts";
import { WordMill } from "./wordmill.ts";
import { LEVELS, levelLabel, type Level } from "./types.ts";
import type { GoalData } from "./api.ts";
import { readClipboardImage } from "./clipboard.ts";

/**
 * An image pasted into the input (Ctrl+V). `seq` ties it to its `[#Image N]`
 * placeholder in the text: only images whose placeholder survives editing are
 * sent with the message.
 */
export interface PastedImage {
  seq: number;
  data: string; // base64
  mime: string;
}

/** The `[#Image N]` placeholder text for a pasted image. */
export function imagePlaceholder(seq: number): string {
  return `[#Image ${seq}]`;
}

// ── boxed input chrome (pure, testable layout builders) ───────────────────────
//
// Mirrors the Grok Build bottom HUD: side-margined expanding box with the
// permission/level label in the top border far right. No model chrome.

/** Outer side margin for the boxed input (cells on left and right). */
export const INPUT_BOX_MARGIN = 1;

/**
 * Neutral grey border chrome (Grok-style) — not brand amber. Soft enough to
 * sit behind content; bright enough to read the box edge.
 */
export function inputBoxBorderColor(): string {
  return "\x1b[38;5;240m";
}

/**
 * Permission level as five dots for the box border (filled = active ceiling).
 * Plain string for width math; pair with `borderLevelDotsStyled` for colour.
 * e.g. level 3 → `●●●○○`
 */
export function borderLevelDots(level: Level): string {
  const n = Math.max(1, Math.min(5, level));
  return "●".repeat(n) + "○".repeat(5 - n);
}

/** Styled dots: filled in `levelColor`, empty in muted grey. */
export function borderLevelDotsStyled(
  level: Level,
  levelColor: string,
  emptyColor = "\x1b[38;5;240m",
  reset = "\x1b[0m"
): string {
  const n = Math.max(1, Math.min(5, level));
  let out = "";
  for (let i = 1; i <= 5; i++) {
    out += i <= n ? `${levelColor}●${reset}` : `${emptyColor}○${reset}`;
  }
  return out;
}

/** @deprecated use borderLevelDots — kept name for call-site greps during transition */
export function borderLevelLabel(level: Level): string {
  return borderLevelDots(level);
}

/**
 * Geometry of the boxed input at a given terminal width.
 * `boxW` is border-to-border width; `contentW` is the wrap width inside.
 * `leftPad` is the screen column where content begins (margin + │ + space).
 */
export function inputBoxGeometry(cols: number): {
  margin: number;
  boxW: number;
  contentW: number;
  leftPad: number;
} {
  const margin = INPUT_BOX_MARGIN;
  // Fit inside the HUD row cap (cols - 1): margin + boxW <= cols - 1.
  const boxW = Math.max(12, cols - 1 - margin);
  // │ + space + content + space + │
  const contentW = Math.max(4, boxW - 4);
  const leftPad = margin + 2;
  return { margin, boxW, contentW, leftPad };
}

/** Visible width helper used by pure box builders (ANSI-aware, wide glyphs). */
function boxVisibleWidth(s: string): number {
  // eslint-disable-next-line no-control-regex
  let w = 0;
  for (const ch of s.replace(/\x1b\[[0-9;]*m/g, "")) {
    const cp = ch.codePointAt(0)!;
    w += isWideCodePoint(cp) ? 2 : 1;
  }
  return w;
}

/**
 * Top border with the level dots right-aligned in the border:
 * `╭──────────── ●●●○○ ─╮`
 *
 * `levelText` is the plain-width form (e.g. `●●●○○`); `levelStyled` is the
 * coloured form drawn in its place. When omitted, `levelText` is painted with
 * `levelColor` as a single run.
 */
export function buildBoxTop(
  boxW: number,
  levelText: string,
  borderColor: string,
  levelColor: string,
  reset = "\x1b[0m",
  levelStyled?: string
): string {
  const labelPlain = ` ${levelText} `;
  const rightFill = 1;
  const budget = Math.max(1, boxW - 2 - rightFill); // corners + right dashes
  const plain =
    labelPlain.length > budget ? labelPlain.slice(0, Math.max(1, budget)) : labelPlain;
  const leftFill = Math.max(1, boxW - 2 - plain.length - rightFill);
  // Styled dots keep the surrounding spaces unstyled so visible width matches.
  const mid =
    levelStyled !== undefined
      ? ` ${levelStyled} `
      : `${levelColor}${plain}${reset}`;
  return (
    `${borderColor}╭${"─".repeat(leftFill)}${reset}` +
    mid +
    `${borderColor}${"─".repeat(rightFill)}╮${reset}`
  );
}

/** Bottom border: `╰────────────────╯` */
export function buildBoxBottom(boxW: number, borderColor: string, reset = "\x1b[0m"): string {
  return `${borderColor}╰${"─".repeat(Math.max(1, boxW - 2))}╯${reset}`;
}

/** Plain top border with no level chrome: `╭────────────────╮` (menus, pickers). */
export function buildBoxTopPlain(boxW: number, borderColor: string, reset = "\x1b[0m"): string {
  return `${borderColor}╭${"─".repeat(Math.max(1, boxW - 2))}╮${reset}`;
}

/**
 * Top border with a LEFT-aligned label in the rim — building block for the
 * {@link Tui.boxedPrompt} component:
 * `╭ Denial feedback ────────────────╮`
 */
export function buildBoxTopLeftLabel(
  boxW: number,
  label: string,
  borderColor: string,
  labelColor = "\x1b[90m",
  reset = "\x1b[0m"
): string {
  const plain = ` ${label} `;
  const budget = Math.max(1, boxW - 3); // ╭ + ≥1 dash + ╮
  const lab = plain.length > budget ? plain.slice(0, Math.max(1, budget)) : plain;
  const fill = Math.max(1, boxW - 2 - lab.length);
  return (
    `${borderColor}╭${reset}` +
    `${labelColor}${lab}${reset}` +
    `${borderColor}${"─".repeat(fill)}╮${reset}`
  );
}

// ── Boxed prompt component ──────────────────────────────────────────────────
//
// Reusable single-line free-text capture that reuses the main input box chrome
// (side margin, grey rounded rim, `❯` carrot, real caret). The only chrome
// difference is a left-aligned label in the top border instead of level dots.
//
// Used today for denial feedback; also the right shape for any other short
// free-text “tell the agent / tell the system” moment (e.g. reject-with-note,
// handoff reason, rename). Prefer this over ad-hoc `prompt()` bars when the
// capture should feel continuous with the HUD.
//
// Layout:
//   ╭ <borderLabel> ─────────────────────────╮
//   │ ❯ typed text…                           │
//   ╰─────────────────────────────────────────╯
//
// Keys: Enter submits (may be empty), Esc clears a non-empty draft then
// cancels on a second Esc (empty buffer). Gradient text is intentionally
// unused here — brand gradient is reserved for success moments.

/** Options for {@link Tui.boxedPrompt}. */
export interface BoxedPromptOptions {
  /**
   * Left-aligned label painted into the top border rim
   * (e.g. `"Denial feedback"`, `"Handoff note"`).
   */
  borderLabel: string;
  /** ANSI colour for the border label. Default: dim grey (`\x1b[90m`). */
  labelColor?: string;
  /** Seed the buffer (e.g. to re-edit a previous note). */
  initial?: string;
  /**
   * When true (default), the first Esc on a non-empty buffer clears it; Esc on
   * an empty buffer cancels (resolves `null`). When false, Esc always cancels.
   */
  escClearsFirst?: boolean;
  /**
   * When true, the caller already owns the keyboard via a takeover (e.g. the
   * approval dialog chaining into feedback). Skips begin/end takeover so the
   * parent can finish its own cleanup. Default false.
   */
  nested?: boolean;
}

/**
 * One body row of the input box: `│ content… │` padded to contentW.
 * `content` may include ANSI (prompt colour); padding uses visible width.
 */
export function buildBoxBody(
  content: string,
  contentW: number,
  borderColor: string,
  reset = "\x1b[0m"
): string {
  const vw = boxVisibleWidth(content);
  const pad = Math.max(0, contentW - vw);
  return `${borderColor}│${reset} ${content}${" ".repeat(pad)} ${borderColor}│${reset}`;
}

/**
 * Wrap prompt + multi-line input into physical body content lines at contentW.
 *
 * Grok-style indent: only the first physical row carries the real prompt; every
 * subsequent row (soft wrap OR hard newline) is indented by `prefixWidth`
 * spaces so text lines up under the first line's content (after `❯ `).
 *
 * Pure: no terminal I/O. Returns at least one line (the bare prefix when empty).
 */
export function wrapInputBodyLines(
  inputBuffer: string,
  prefix: string,
  prefixWidth: number,
  contentW: number
): string[] {
  const indent = " ".repeat(Math.max(0, prefixWidth));
  const lines = inputBuffer.split("\n");
  const out: string[] = [];
  let isFirstPhysical = true;
  for (let i = 0; i < lines.length; i++) {
    const text = lines[i];
    let remaining = text;
    // Always emit at least one physical row per logical line.
    do {
      const lead = isFirstPhysical ? prefix : indent;
      const avail = Math.max(0, contentW - prefixWidth);
      let take = Math.min(remaining.length, avail);
      if (take === 0 && avail === 0 && remaining.length > 0) take = 1;
      const chunk = remaining.slice(0, take);
      remaining = remaining.slice(take);
      out.push(lead + chunk);
      isFirstPhysical = false;
      if (remaining.length === 0) break;
    } while (remaining.length > 0);
  }
  if (out.length === 0) out.push(prefix);
  return out;
}

/**
 * Greyscale highlight for the working border sweep.
 * intensity 0 = rest grey, 1 = near-white head. No brand hues — just contrast.
 */
export function borderHighlightGrey(intensity: number): string {
  const t = Math.max(0, Math.min(1, intensity));
  // Ease so the head reads as a bright “bead” and the trail softens quickly.
  const e = t * t;
  const lo = 88; // rest-adjacent
  const hi = 236; // soft white
  const v = Math.round(lo + (hi - lo) * e);
  // Prefer truecolor greys when available; 256 greyscale ramp otherwise.
  const ct = process.env.COLORTERM ?? "";
  const tc =
    /truecolor|24bit/i.test(ct) ||
    /iterm|kitty|wezterm|ghostty|alacritty/i.test(process.env.TERM_PROGRAM ?? "");
  if (tc) return `\x1b[38;2;${v};${v};${v}m`;
  // xterm 256 greyscale: 232 (dark) … 255 (white)
  return `\x1b[38;5;${232 + Math.round(23 * e)}m`;
}

/**
 * Color a perimeter cell for the rotating working highlight.
 * `phase` 0..1 is the head position (clockwise from top-left corner).
 * A short bright grey/white trail rides the rim; the rest stays dull grey.
 */
export function rotatingBorderCellColor(
  index: number,
  perimeter: number,
  phase: number,
  trailFrac = 0.2
): string {
  const P = Math.max(1, perimeter);
  const pos = index / P;
  // Distance behind the head along the travel direction (0 at head → 1 full lap).
  const dBehind = ((phase - pos) % 1 + 1) % 1;
  if (dBehind <= trailFrac) {
    // 1 at head, 0 at tail end
    return borderHighlightGrey(1 - dBehind / trailFrac);
  }
  return inputBoxBorderColor();
}

/**
 * Clockwise perimeter index for a border cell.
 * Path: top L→R, right T→B (body only), bottom R→L, left B→T (body only).
 * Corners live on the top/bottom edges only (not duplicated on the sides).
 */
export function perimeterIndex(
  edge: "top" | "right" | "bottom" | "left",
  offset: number,
  boxW: number,
  bodyH: number
): number {
  const W = boxW;
  const H = Math.max(1, bodyH);
  // top: 0..W-1, right: W..W+H-1, bottom: W+H..2W+H-1, left: 2W+H..2W+2H-1
  if (edge === "top") return Math.max(0, Math.min(W - 1, offset));
  if (edge === "right") return W + Math.max(0, Math.min(H - 1, offset));
  if (edge === "bottom") return W + H + Math.max(0, Math.min(W - 1, offset));
  return 2 * W + H + Math.max(0, Math.min(H - 1, offset));
}

/**
 * Full boxed-input row set (top + body + bottom), each pre-prefixed with the
 * side margin. Pure builder for tests and `renderBottom`.
 *
 * When `borderPhase` is a finite number, a grey/white highlight sweeps the
 * border clockwise (working state). Otherwise the border is static grey.
 */
export function buildInputBoxRows(opts: {
  cols: number;
  inputBuffer: string;
  prefix: string;
  prefixWidth: number;
  level: Level;
  levelColor: string;
  borderColor?: string;
  /** 0..1 head position for the rotating border highlight; omit/null when idle. */
  borderPhase?: number | null;
}): string[] {
  const { cols, inputBuffer, prefix, prefixWidth, level, levelColor } = opts;
  const borderColor = opts.borderColor ?? inputBoxBorderColor();
  const geo = inputBoxGeometry(cols);
  const body = wrapInputBodyLines(inputBuffer, prefix, prefixWidth, geo.contentW);
  const pad = " ".repeat(geo.margin);
  const phase = opts.borderPhase;

  if (phase == null || !Number.isFinite(phase)) {
    const dots = borderLevelDots(level);
    const dotsStyled = borderLevelDotsStyled(level, levelColor);
    const top = buildBoxTop(geo.boxW, dots, borderColor, levelColor, "\x1b[0m", dotsStyled);
    const bottom = buildBoxBottom(geo.boxW, borderColor);
    return [pad + top, ...body.map((b) => pad + buildBoxBody(b, geo.contentW, borderColor)), pad + bottom];
  }

  // ── animated perimeter (one continuous clockwise loop) ────────────────────
  //
  //   0 ── top L→R ──▶ W-1
  //   ▲                  │
  //   left B→T        right T→B
  //   │                  ▼
  //   2W+2H-1 ◀─ bottom R→L ─ W+H
  //
  const W = geo.boxW;
  const bodyH = Math.max(1, body.length);
  const P = 2 * W + 2 * bodyH;
  const reset = "\x1b[0m";
  const colorAt = (i: number) => rotatingBorderCellColor(i, P, phase);

  // Top: every column 0..W-1 is a perimeter step. Level dots keep their own
  // colours but still occupy indices so the head travels through that span.
  const dots = borderLevelDots(level);
  const dotsStyled = borderLevelDotsStyled(level, levelColor);
  const labelPlain = ` ${dots} `;
  const rightFillN = 1;
  const budget = Math.max(1, W - 2 - rightFillN);
  const plain = labelPlain.length > budget ? labelPlain.slice(0, Math.max(1, budget)) : labelPlain;
  const leftFill = Math.max(1, W - 2 - plain.length - rightFillN);
  // Layout: [╭][─×leftFill][plain][─×rightFill][╮]  length = W
  let top = "";
  let xi = 0;
  top += colorAt(perimeterIndex("top", xi++, W, bodyH)) + "╭" + reset;
  for (let k = 0; k < leftFill; k++) {
    top += colorAt(perimeterIndex("top", xi++, W, bodyH)) + "─" + reset;
  }
  // Dots block: advance indices for each visible column, paint spaces with the
  // sweep colour and leave the glyph colours from dotsStyled.
  // plain is " ●●●○○ " — rebuild so each cell gets the right perimeter colour
  // under the fixed glyphs.
  {
    const plainChars = [...plain];
    const styledInner = dotsStyled; // just the 5 dots, no surrounding spaces
    let di = 0; // index into filled/empty dots
    for (const ch of plainChars) {
      const c = colorAt(perimeterIndex("top", xi++, W, bodyH));
      if (ch === "●" || ch === "○") {
        // Keep level colour on the glyph; still count the step for travel.
        // Prefer level-styled sequence if available.
        const levelN = Math.max(1, Math.min(5, level));
        const filled = di < levelN;
        di++;
        top +=
          (filled ? levelColor || c : "\x1b[38;5;240m") +
          (filled ? "●" : "○") +
          reset;
        void styledInner;
      } else {
        top += c + ch + reset;
      }
    }
  }
  for (let k = 0; k < rightFillN; k++) {
    top += colorAt(perimeterIndex("top", xi++, W, bodyH)) + "─" + reset;
  }
  top += colorAt(perimeterIndex("top", Math.min(xi, W - 1), W, bodyH)) + "╮" + reset;

  // Body: right edge T→B (offset y), left edge B→T (offset bodyH-1-y).
  const bodyRows: string[] = [];
  for (let y = 0; y < bodyH; y++) {
    const leftC = colorAt(perimeterIndex("left", bodyH - 1 - y, W, bodyH));
    const rightC = colorAt(perimeterIndex("right", y, W, bodyH));
    const vw = boxVisibleWidth(body[y]);
    const sp = Math.max(0, geo.contentW - vw);
    bodyRows.push(
      `${pad}${leftC}│${reset} ${body[y]}${" ".repeat(sp)} ${rightC}│${reset}`
    );
  }

  // Bottom: clockwise means RIGHT → LEFT after finishing the right edge.
  // offset 0 = bottom-right ╯, offset W-1 = bottom-left ╰.
  let bottom = "";
  for (let x = 0; x < W; x++) {
    // visual column from right to left for index, but we emit left-to-right:
    // visual x maps to clockwise offset (W-1-x)
    const idx = perimeterIndex("bottom", W - 1 - x, W, bodyH);
    const c = colorAt(idx);
    if (x === 0) bottom += c + "╰" + reset;
    else if (x === W - 1) bottom += c + "╯" + reset;
    else bottom += c + "─" + reset;
  }

  return [pad + top, ...bodyRows, pad + bottom];
}

const C = {
  reset: "\x1b[0m",
  dim: "\x1b[2m",
  bold: "\x1b[1m",
  cyan: "\x1b[36m",
  green: "\x1b[32m",
  yellow: "\x1b[33m",
  red: "\x1b[31m",
  blue: "\x1b[34m",
  magenta: "\x1b[35m",
  gray: "\x1b[90m",
  teal: "\x1b[38;5;37m",
};

// Dense braille frames — they fill more of the cell, so the spinner reads
// clearly instead of looking like faint dots.
const FRAMES = ["⣷", "⣯", "⣟", "⡿", "⢿", "⣻", "⣽", "⣾"];
// Animation tick for the spinners (frame advance + redraw interval). Snappier
// than the old 100ms without burning noticeable CPU on redraws.
const SPINNER_MS = 70;

// Distinct hues cycled across concurrent subagents so each one reads as its own
// stream (loader + label share the colour). Cyan is reserved for the main
// working loader and orange for the compaction agent, so both are intentionally
// absent here.
const SUBAGENT_COLORS = [
  "\x1b[35m", // magenta
  "\x1b[38;5;39m", // azure
  "\x1b[38;5;75m", // blue
  "\x1b[32m", // green
  "\x1b[38;5;141m", // violet
  "\x1b[38;5;177m", // orchid
];

// The compaction subagent is ALWAYS orange — a fixed signature colour so the
// user instantly recognizes background context compaction, session after
// session, regardless of what else is running.
const COMPACTION_COLOR = "\x1b[38;5;208m";
const COMPACTION_AGENT = "compaction_agent";

type KeyHandler = (str: string | undefined, key: readline.Key) => void;

/**
 * Approximate wcwidth: code points that render TWO terminal columns — CJK,
 * Hangul, fullwidth forms, and the emoji blocks. Everything else (including
 * ambiguous-width glyphs like box drawing, arrows, and the ⚙/⏳ badges, which
 * the terminals we target render narrow by default) counts one. Used by the
 * HUD width math so a wide character can't silently push a row past the
 * terminal edge and desync the redraw.
 */
function isWideCodePoint(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x115f) || // Hangul Jamo
    (cp >= 0x2e80 && cp <= 0xa4cf) || // CJK radicals … Yi
    (cp >= 0xac00 && cp <= 0xd7a3) || // Hangul syllables
    (cp >= 0xf900 && cp <= 0xfaff) || // CJK compatibility ideographs
    (cp >= 0xfe30 && cp <= 0xfe4f) || // CJK compatibility forms
    (cp >= 0xff00 && cp <= 0xff60) || // fullwidth forms
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x1f300 && cp <= 0x1faff) || // emoji
    cp >= 0x20000 // CJK extension planes
  );
}

/**
 * Neutralize control characters in a HUD row. An embedded newline (e.g. a
 * garbled multi-line step label when an upstream tool-call parse fails) makes
 * one "row" occupy several physical rows; the region-height math counts one,
 * so every subsequent redraw stamps the region's top rows into the scrollback
 * — the "same two lines repeated for pages" failure mode. Tabs are just as
 * bad (the terminal expands them to an uncounted width). Every control char
 * except the SGR escapes becomes a single space, and a stray ESC that isn't
 * starting a CSI sequence is dropped entirely.
 */
function sanitizeHudRow(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x1b(?!\[)/g, "").replace(/[\x00-\x1a\x1c-\x1f\x7f]/g, " ");
}

/**
 * A slash command shown in the inline command palette. `name` is the word typed
 * after `/` (e.g. "compact"); typing filters the list by it. `hint` may be a
 * function so dynamic state (counts, level) stays fresh as the palette redraws.
 */
export interface SlashCommand {
  name: string;
  label: string;
  hint?: string | (() => string);
  run: () => void | Promise<void>;
}

export class Tui {
  // input + indicators
  private inputBuffer = "";
  private cursorPos = 0; // caret index within inputBuffer (0..length)
  private lastCursorRow = 0; // caret's row offset from the input region top, last render
  private working = false;
  private workingStart = 0;
  // Slot-machine morph for the working label (Working → Brewing → …).
  private wordMill = new WordMill();
  private spinnerTimer: ReturnType<typeof setInterval> | null = null;
  private bgCount = 0;
  // The `[⚙ n bg]` prompt badge can be selected with ← from the start of the
  // input (rendered inverted); Enter then opens the background-process panel.
  private bgBadgeSelected = false;
  private queuedCount = 0;
  private subagents: { id: string; label: string; agentName?: string }[] = []; // active subagents (one line each)
  private subagentColorByID = new Map<string, number>(); // subagent id → SUBAGENT_COLORS index
  private tokensIn = 0; // cumulative input tokens
  private tokensOut = 0; // cumulative output tokens (includes the in-progress live count)
  private contextPct: number | null = null; // % of the model context window currently used

  // Live streaming preview, shown just above the status line while a turn runs:
  // the model's internal reasoning (dim italic) until the answer starts, then the
  // answer text (plain). Bounded to a tail; cleared when the message commits.
  private streamThinking = "";
  private streamResponse = "";
  private streamMessageId: string | null = null; // the message currently previewing
  private streamRedrawTimer: ReturnType<typeof setTimeout> | null = null;
  private streamIdleTimer: ReturnType<typeof setTimeout> | null = null; // wipes a stale preview

  // Live goal checklist, fixed below the status bar. Driven by the goal_updated
  // thread event (+ an initial fetch). null = nothing to show.
  private goal: GoalData | null = null;
  // Set true the moment every step of a goal is done: the goal area is cleared
  // and the status line shows "Goal complete." until the next turn starts.
  private goalComplete = false;

  // Inline slash-command palette: when the input starts with "/", the filtered
  // command list renders above the prompt and arrows/enter/tab drive it.
  private commands: SlashCommand[] = [];
  private slashIdx = 0; // highlighted index within the FILTERED command list
  private step: string | null = null; // current step label (e.g. "writing index.html")
  private stepStart = 0; // when the current step began (for the step's own elapsed)
  private stepOut = 0; // output tokens produced during the current step
  private connected = true;
  private bottomDrawn = false;
  private started = false;
  // Resize bookkeeping. When the terminal width changes, previously drawn HUD
  // rows re-wrap (a full-width ruler becomes 2+ physical rows when narrowed),
  // so the caret-relative move-up from the last paint is stale. We store the
  // visible width of EVERY region row (not just above the body) plus the caret
  // row index so moveToRegionTop can recompute physical height under the new
  // wrap. Resize events are debounced — drag-resizing fires dozens of events
  // and redrawing each one desyncs and leaves ghost chrome.
  private lastDrawnCols = 0;
  /** Visible width of every HUD row from the last paint, top → bottom. */
  private drawnRegionWidths: number[] = [];
  /** Index into drawnRegionWidths of the row the caret sat on last paint. */
  private lastCaretRegionIndex = 0;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  /** True between a resize event and the debounced repaint — spinner ticks no-op. */
  private resizePending = false;
  /** @deprecated kept as alias during paint — prefer drawnRegionWidths */
  private drawnHudWidths: number[] = [];

  // takeover (approval / menu) state
  private takeoverHandler: KeyHandler | null = null;
  private bufferedPrints: string[] = [];

  // double-press-to-quit state: the first ctrl-c arms a brief window and shows a
  // transient hint; a second ctrl-c within the window actually quits.
  private quitArmed = false;
  private quitTimer: ReturnType<typeof setTimeout> | null = null;

  // bracketed-paste state
  private pasting = false;
  private pasteTimer: ReturnType<typeof setTimeout> | null = null;

  // Images pasted into the CURRENT input (Ctrl+V). Each got a `[#Image N]`
  // placeholder at the caret; on submit only images whose placeholder is still
  // present in the text are handed to onSubmit. Cleared with the input.
  private pendingImages: PastedImage[] = [];
  private imagePasteBusy = false; // one clipboard read at a time

  // Sent-message history for ↑/↓ recall (oldest → newest). `historyIdx` is the
  // entry currently shown (null = not browsing); the in-progress draft is
  // stashed so cycling past the newest entry restores it. Any edit exits
  // browsing and keeps the recalled text as the new draft.
  private history: string[] = [];
  private historyIdx: number | null = null;
  private historyDraft = "";
  private historyDraftImages: PastedImage[] = [];

  // event hooks (wired by index.ts)
  onSubmit: (text: string, images: PastedImage[]) => void = () => {};
  onInterrupt: () => void = () => {};
  /** Up on the top row: return true to consume it (e.g. pull a queued message)
   *  before history recall gets a chance. */
  onUpArrow: () => boolean = () => false;
  /** Enter while the `[⚙ n bg]` badge is selected — opens the bg process panel. */
  onBgBadge: () => void = () => {};
  private onQuit: () => void = () => process.exit(0);
  private levelListeners: ((l: Level) => void)[] = [];

  constructor(public level: Level = 1) {
    readline.emitKeypressEvents(process.stdin);
    if (process.stdin.isTTY) process.stdin.setRawMode(true);
    process.stdin.on("keypress", (str, key) => this.dispatch(str, key));
    process.stdin.resume();
    // Enable bracketed paste so a paste arrives atomically (newlines inside a
    // paste don't fire a premature submit) and restore it on exit.
    process.stdout.write("\x1b[?2004h");
    // Restore bracketed paste + the cursor on exit (e.g. ctrl-c mid-menu).
    process.on("exit", () => process.stdout.write("\x1b[?2004l\x1b[?25h"));
    // Debounced resize redraw — see scheduleResizeRedraw().
    process.stdout.on("resize", () => this.scheduleResizeRedraw());
  }

  /**
   * Terminal resize fires continuously while the user drags. Redrawing on every
   * event desyncs the region-height math (each paint uses a half-rewrapped
   * intermediate width) and stamps ghost boxes into the scrollback. Wait for
   * the size to settle (~1–2 frames), then do one clean clear+repaint. While
   * pending, spinner ticks skip paint so they don't thrash mid-drag.
   */
  private scheduleResizeRedraw(): void {
    this.resizePending = true;
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null;
      this.resizePending = false;
      if (!this.started || this.takeoverHandler) return;
      this.renderBottom();
    }, 40);
  }

  get colors() {
    return C;
  }

  setQuitHandler(fn: () => void): void {
    this.onQuit = fn;
  }

  /**
   * Handle a ctrl-c. The first press arms a 2-second window and surfaces a
   * transient "Press Control-C again to exit" hint in the bottom region; a
   * second press within the window quits. After the window lapses the hint
   * clears and the next ctrl-c starts over — so it always takes two.
   */
  requestQuit(): void {
    if (this.quitArmed) {
      if (this.quitTimer) clearTimeout(this.quitTimer);
      this.quitTimer = null;
      this.quitArmed = false;
      this.onQuit();
      return;
    }
    this.quitArmed = true;
    this.renderBottom();
    this.quitTimer = setTimeout(() => {
      this.quitArmed = false;
      this.quitTimer = null;
      this.renderBottom();
    }, 2000);
  }

  /** Tear down the bottom region and restore the terminal (called on quit). */
  end(): void {
    // Freeze first so any in-flight spinner / stream / quit timers cannot
    // re-paint the boxed HUD after we clear it (quit is async and awaits
    // backend stop for up to ~1.5s — a 70ms spinner tick would otherwise
    // redraw the input box and garble the farewell message into it).
    this.started = false;
    this.working = false;
    this.subagents = [];
    if (this.quitTimer) clearTimeout(this.quitTimer);
    this.quitTimer = null;
    if (this.streamIdleTimer) clearTimeout(this.streamIdleTimer);
    this.streamIdleTimer = null;
    if (this.streamRedrawTimer) {
      clearTimeout(this.streamRedrawTimer);
      this.streamRedrawTimer = null;
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    if (this.spinnerTimer) {
      clearInterval(this.spinnerTimer);
      this.spinnerTimer = null;
    }
    this.clearBottom();
    process.stdout.write("\x1b[?2004l\x1b[?25h");
  }
  onLevelChange(fn: (l: Level) => void): void {
    this.levelListeners.push(fn);
  }

  /** Carrot colour by level — cooler/safer (low) to warmer/permissive (high). */
  levelColor(): string {
    return { 1: C.cyan, 2: C.green, 3: C.yellow, 4: C.magenta, 5: C.red }[this.level];
  }

  // ─── key dispatch ──────────────────────────────────────────────────────────

  private dispatch(str: string | undefined, key: readline.Key): void {
    const seq = (key && key.sequence) || str || "";

    // Shift+Enter inserts a newline instead of submitting. The robust signal is a
    // bare LF ("\n"): Ghostty/iTerm2/WezTerm and many others send "\n" for
    // Shift+Enter (often by default, otherwise configurable) while plain Enter
    // sends CR ("\r"). We also accept the Kitty ("\x1b[13;2u") and
    // modifyOtherKeys ("\x1b[27;2;13~") forms for terminals that emit those.
    // Checked before any other key logic (including paste detection) — and a raw
    // "\n" never reaches the Enter/submit handler below.
    if (seq === "\n" || seq === "\x1b[13;2u" || seq === "\x1b[27;2;13~") {
      this.insertAtCursor("\n");
      return;
    }

    if (key && key.ctrl && key.name === "c") {
      this.requestQuit();
      return;
    }
    if (key && key.name === "tab" && key.shift) {
      this.cycleLevel();
      return;
    }

    // Bracketed paste: everything between the start/end markers is literal text,
    // even newlines. Handle it before any other key logic so a pasted Return
    // doesn't submit and a pasted "/" doesn't open the menu.
    if (!this.pasting && seq.includes("\x1b[200~")) {
      this.pasting = true;
      this.armPasteSafety();
      this.handlePasteChunk(seq.slice(seq.indexOf("\x1b[200~") + 6));
      return;
    }
    if (this.pasting) {
      this.armPasteSafety();
      this.handlePasteChunk(seq);
      return;
    }

    // A takeover (approval/menu) owns the keyboard while active.
    if (this.takeoverHandler) {
      this.takeoverHandler(str, key);
      return;
    }
    if (!key) return;

    // While the `[⚙ n bg]` badge is selected, Enter opens the background-process
    // panel, → / Escape step back into the input, ← stays put (nothing further
    // left), and any other key deselects and is handled as normal input.
    if (this.bgBadgeSelected) {
      if (key.name === "return" || key.name === "enter") {
        this.bgBadgeSelected = false;
        this.renderBottom();
        this.onBgBadge();
        return;
      }
      if (key.name === "left") return;
      this.bgBadgeSelected = false;
      if (key.name === "right" || key.name === "escape") {
        this.renderBottom();
        return;
      }
      this.renderBottom();
      // fall through: the key edits/moves as usual
    }

    // Inline slash-command palette owns navigation while the input starts with
    // "/". Editing keys (printable/backspace/left/right) fall through to the
    // normal input logic below, which re-filters the palette on the next render.
    if (this.paletteOpen()) {
      const matches = this.filteredCommands();
      const cur = matches.length ? Math.min(this.slashIdx, matches.length - 1) : 0;
      if (key.name === "up") {
        if (matches.length) {
          this.slashIdx = (cur - 1 + matches.length) % matches.length;
          this.renderBottom();
        }
        return;
      }
      if (key.name === "down") {
        if (matches.length) {
          this.slashIdx = (cur + 1) % matches.length;
          this.renderBottom();
        }
        return;
      }
      if (key.name === "tab") {
        if (matches.length) {
          this.inputBuffer = "/" + matches[cur].name;
          this.cursorPos = this.inputBuffer.length;
          this.slashIdx = 0;
          this.renderBottom();
        }
        return;
      }
      if (key.name === "return" || key.name === "enter") {
        // Shift+Enter inserts a newline instead of running the command
        if (key.shift) {
          this.insertAtCursor("\n");
          return;
        }
        if (matches.length) this.runCommand(matches[cur]);
        return; // never submit a "/…" line as a chat message
      }
      if (key.name === "escape") {
        this.inputBuffer = "";
        this.cursorPos = 0;
        this.slashIdx = 0;
        this.renderBottom();
        return;
      }
      // any other key falls through to normal editing (palette re-filters live)
    }

    if (key.name === "escape") {
      this.onInterrupt();
      return;
    }
    // Caret movement within the (possibly wrapped) input.
    if (key.name === "left") {
      if (this.cursorPos > 0) {
        this.cursorPos--;
        this.renderBottom();
      } else if (this.bgCount > 0) {
        // Caret already at the start — ← "selects" the [⚙ n bg] badge.
        this.bgBadgeSelected = true;
        this.renderBottom();
      }
      return;
    }
    if (key.name === "right") {
      if (this.cursorPos < this.inputBuffer.length) {
        this.cursorPos++;
        this.renderBottom();
      }
      return;
    }
    if (key.name === "home" || (key.ctrl && key.name === "a")) {
      this.cursorPos = 0;
      this.renderBottom();
      return;
    }
    if (key.name === "end" || (key.ctrl && key.name === "e")) {
      this.cursorPos = this.inputBuffer.length;
      this.renderBottom();
      return;
    }
    // Up: move the caret up a visual row when it isn't on the top one —
    // history recall must never hijack cursor movement inside multi-line text.
    // On the TOP row: first offer the key to onUpArrow (pulling a queued
    // message back), then recall the previous history entry.
    if (key.name === "up") {
      const { caretRow } = this.inputLayout();
      if (caretRow > 0) {
        this.moveCaretVertical(-1);
        return;
      }
      if (this.onUpArrow()) return;
      this.historyPrev();
      return;
    }
    // Down mirrors up: move the caret down a row unless it's on the bottom
    // one; from the bottom row, step forward through history (past the newest
    // entry restores whatever was being typed).
    if (key.name === "down") {
      const { caretRow, rowCount } = this.inputLayout();
      if (caretRow < rowCount - 1) {
        this.moveCaretVertical(1);
        return;
      }
      this.historyNext();
      return;
    }
    if (key.name === "return" || key.name === "enter") {
      // Shift+Enter inserts a newline instead of submitting.
      // Detection: some terminals set key.shift, others send a distinct escape sequence.
      // Protocol-based Shift+Enter is already handled at the top of dispatch();
      // this catches terminals that natively set the shift modifier on Return.
      if (key.shift) {
        this.insertAtCursor("\n");
        return;
      }
      const text = this.inputBuffer;
      // Only images whose placeholder survived editing ride along.
      const images = this.pendingImages.filter((img) => text.includes(imagePlaceholder(img.seq)));
      this.inputBuffer = "";
      this.cursorPos = 0;
      this.pendingImages = [];
      this.historyIdx = null;
      this.historyDraft = "";
      this.historyDraftImages = [];
      this.renderBottom();
      if (text.trim()) {
        this.addHistoryEntry(text.trim());
        this.onSubmit(text.trim(), images);
      }
      return;
    }
    // Ctrl+V: paste an image from the system clipboard. Terminals only deliver
    // TEXT through bracketed paste, so an image needs this explicit ask — we
    // read the clipboard and drop an `[#Image N]` placeholder at the caret.
    if (key.ctrl && key.name === "v") {
      void this.pasteClipboardImage();
      return;
    }
    if (key.name === "backspace") {
      if (this.cursorPos > 0) {
        this.inputBuffer =
          this.inputBuffer.slice(0, this.cursorPos - 1) + this.inputBuffer.slice(this.cursorPos);
        this.cursorPos--;
        this.slashIdx = 0;
        this.historyIdx = null;
        this.renderBottom();
      }
      return;
    }
    if (key.name === "delete") {
      if (this.cursorPos < this.inputBuffer.length) {
        this.inputBuffer =
          this.inputBuffer.slice(0, this.cursorPos) + this.inputBuffer.slice(this.cursorPos + 1);
        this.slashIdx = 0;
        this.historyIdx = null;
        this.renderBottom();
      }
      return;
    }
    if (key.ctrl || key.meta || key.name === "tab") return;
    if (str && str >= " ") {
      this.insertAtCursor(str);
    }
  }

  private insertAtCursor(text: string): void {
    this.inputBuffer = this.inputBuffer.slice(0, this.cursorPos) + text + this.inputBuffer.slice(this.cursorPos);
    this.cursorPos += text.length;
    this.slashIdx = 0; // re-anchor the palette highlight to the top match as the filter changes
    this.historyIdx = null; // an edit exits history browsing; the text is the new draft
    this.renderBottom();
  }

  /** Insert a paste fragment at the caret; collapse newlines (single-line input). */
  private handlePasteChunk(chunk: string): void {
    const end = chunk.indexOf("\x1b[201~");
    const content = (end >= 0 ? chunk.slice(0, end) : chunk).replace(/[\r\n]+/g, " ");
    if (content) {
      this.inputBuffer =
        this.inputBuffer.slice(0, this.cursorPos) + content + this.inputBuffer.slice(this.cursorPos);
      this.cursorPos += content.length;
      this.historyIdx = null;
    }
    if (end >= 0) {
      this.pasting = false;
      if (this.pasteTimer) {
        clearTimeout(this.pasteTimer);
        this.pasteTimer = null;
      }
    }
    this.renderBottom();
  }

  /** Never let a missed end-marker wedge the input: clear paste mode shortly. */
  private armPasteSafety(): void {
    if (this.pasteTimer) clearTimeout(this.pasteTimer);
    this.pasteTimer = setTimeout(() => {
      this.pasting = false;
      this.pasteTimer = null;
      this.renderBottom();
    }, 2000);
  }

  // ─── input layout + vertical caret movement ────────────────────────────────

  /**
   * The input's physical rows (same wrapping math as the boxed input body:
   * logical lines split on "\n", line 0 led by the prompt prefix, each wrapping
   * at the box content width) plus where the caret sits among them. Each row
   * records the buffer index of its first character, its character count, and
   * the visual column its first character renders at (only the first physical
   * row of line 0 is offset, by the prompt). Drives ↑/↓: row 0 is "the top
   * line" (history recall territory), anything below moves the caret instead.
   */
  private inputLayout(): {
    rows: { start: number; len: number; colOffset: number }[];
    caretRow: number;
    caretCol: number;
    rowCount: number;
  } {
    const cols = process.stdout.columns || 80;
    const { contentW } = inputBoxGeometry(cols);
    const pw = this.visibleWidth(this.promptPrefix());
    const lines = this.inputBuffer.split("\n");
    const rows: { start: number; len: number; colOffset: number }[] = [];
    let offset = 0;
    for (let i = 0; i < lines.length; i++) {
      const len = lines[i].length;
      // Match wrapInputBodyLines: every physical row reserves prefixWidth at the
      // left (real prompt on the first row, spaces on later rows) so hard
      // newlines and soft wraps line up under the first line's text.
      let remaining = len;
      let charStart = 0;
      do {
        const avail = Math.max(0, contentW - pw);
        let take = Math.min(remaining, avail);
        if (take === 0 && avail === 0 && remaining > 0) take = 1;
        rows.push({ start: offset + charStart, len: take, colOffset: pw });
        charStart += take;
        remaining -= take;
      } while (remaining > 0);
      offset += len + 1; // +1 consumes the "\n"
    }
    // Locate the caret among the physical rows.
    let caretRow = 0;
    let caretCol = 0;
    let found = false;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const rowEnd = row.start + row.len;
      const isLast = r === rows.length - 1;
      // Caret lands on this row when it falls inside [start, end), or at `end`
      // when this is the last row OR the next row starts after end (hard newline
      // gap — next row.start === end+1 after the "\n").
      const nextStart = isLast ? rowEnd : rows[r + 1].start;
      if (this.cursorPos < rowEnd || (this.cursorPos === rowEnd && (isLast || nextStart > rowEnd))) {
        caretRow = r;
        caretCol = row.colOffset + (this.cursorPos - row.start);
        found = true;
        break;
      }
    }
    if (!found && rows.length) {
      caretRow = rows.length - 1;
      const row = rows[caretRow];
      caretCol = row.colOffset + row.len;
    }
    // Caret exactly on a wrap boundary past the last row — synthesize an empty
    // trailing row so vertical movement stays in bounds.
    while (caretRow >= rows.length) {
      rows.push({ start: this.inputBuffer.length, len: 0, colOffset: 0 });
    }
    return { rows, caretRow, caretCol, rowCount: rows.length };
  }

  /** Move the caret one visual row up/down, keeping the column when possible. */
  private moveCaretVertical(delta: -1 | 1): void {
    const { rows, caretRow, caretCol } = this.inputLayout();
    const target = caretRow + delta;
    if (target < 0 || target >= rows.length) return;
    const row = rows[target];
    const within = Math.max(0, Math.min(caretCol - row.colOffset, row.len));
    this.cursorPos = Math.min(row.start + within, this.inputBuffer.length);
    this.renderBottom();
  }

  // ─── sent-message history (↑/↓ recall) ─────────────────────────────────────

  /** Seed the recall history (oldest → newest), e.g. from the on-disk file. */
  setHistory(entries: string[]): void {
    this.history = entries.filter((e) => e.trim() !== "");
  }

  /** Record a sent message (skipping a consecutive duplicate). */
  private addHistoryEntry(text: string): void {
    if (this.history[this.history.length - 1] === text) return;
    this.history.push(text);
  }

  /** Recall the previous (older) history entry; stashes the draft first. */
  private historyPrev(): void {
    if (!this.history.length) return;
    if (this.historyIdx === null) {
      this.historyDraft = this.inputBuffer;
      this.historyDraftImages = this.pendingImages;
      this.historyIdx = this.history.length - 1;
    } else if (this.historyIdx > 0) {
      this.historyIdx--;
    } else {
      return; // already at the oldest entry
    }
    this.pendingImages = [];
    this.inputBuffer = this.history[this.historyIdx];
    this.cursorPos = this.inputBuffer.length;
    this.slashIdx = 0;
    this.renderBottom();
  }

  /** Step toward the newest entry; past it, restore the stashed draft. */
  private historyNext(): void {
    if (this.historyIdx === null) return; // not browsing — down does nothing
    if (this.historyIdx < this.history.length - 1) {
      this.historyIdx++;
      this.pendingImages = [];
      this.inputBuffer = this.history[this.historyIdx];
    } else {
      this.historyIdx = null;
      this.inputBuffer = this.historyDraft;
      this.pendingImages = this.historyDraftImages;
      this.historyDraft = "";
      this.historyDraftImages = [];
    }
    this.cursorPos = this.inputBuffer.length;
    this.slashIdx = 0;
    this.renderBottom();
  }

  // ─── clipboard image paste (Ctrl+V) ────────────────────────────────────────

  /**
   * Read an image off the system clipboard and drop an `[#Image N]`
   * placeholder at the caret. The bytes ride along with the message on submit
   * (as a real attachment) as long as the placeholder is still in the text —
   * delete the placeholder and the image is dropped too.
   */
  private async pasteClipboardImage(): Promise<void> {
    if (this.imagePasteBusy) return;
    this.imagePasteBusy = true;
    try {
      const img = await readClipboardImage();
      if (!img) {
        this.print(`${C.dim}No image on the clipboard.${C.reset}`);
        return;
      }
      const seq = this.pendingImages.reduce((m, i) => Math.max(m, i.seq), 0) + 1;
      this.pendingImages.push({ seq, data: img.data, mime: img.mime });
      this.insertAtCursor(imagePlaceholder(seq));
    } finally {
      this.imagePasteBusy = false;
    }
  }

  private cycleLevel(): void {
    const idx = LEVELS.indexOf(this.level);
    this.setLevel(LEVELS[(idx + 1) % LEVELS.length]);
  }

  setLevel(level: Level): void {
    if (level === this.level) return;
    this.level = level;
    this.levelListeners.forEach((fn) => fn(this.level));
    this.print(`${this.levelColor()}${levelLabel(this.level)}${C.reset}`);
  }

  // ─── bottom input line ───────────────────────────────────────────────────

  /** Begin showing the persistent input line. */
  start(): void {
    this.started = true;
    this.renderBottom();
  }

  /** Format an elapsed millisecond span as 45s / 2m 05s / 1h 05m. */
  private formatElapsed(ms: number): string {
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${String(m % 60).padStart(2, "0")}m`;
  }

  /** Compact token count: 945 → "945", 12345 → "12.35k", 1_250_000 → "1.25M". */
  private fmtTokens(n: number): string {
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${(n / 1000).toFixed(2)}k`;
    return `${(n / 1_000_000).toFixed(2)}M`;
  }

  private spinnerFrame(): string {
    // The main loader is a brand moment: the glyph spins fast and its colour
    // drifts through the brand gradient in step with the shimmering label.
    const now = Date.now();
    return `${C.bold}${brandCycleColor(now)}${FRAMES[Math.floor(now / SPINNER_MS) % FRAMES.length]}${C.reset}`;
  }

  /** "↑X ↓Y" cumulative token totals (greyed — low-priority). */
  private tokensText(): string {
    const parts: string[] = [];
    if (this.tokensIn > 0) parts.push(`↑${this.fmtTokens(this.tokensIn)}`);
    if (this.tokensOut > 0) parts.push(`↓${this.fmtTokens(this.tokensOut)}`);
    return parts.length ? `${C.gray}${parts.join(" ")}${C.reset}` : "";
  }

  /**
   * Context-to-compaction gauge, right-aligned on the status line: just "N%"
   * in dull grey. Shown whenever we know a value (any fill — 5%, 10%, 20%…).
   * The percentage is scaled so 100% = where background compaction triggers,
   * not the raw model window.
   */
  private contextGaugeText(): string {
    if (this.contextPct == null) return "";
    // Soft 256-grey — readable but never competing with spinner / tokens.
    return `\x1b[38;5;240m${this.contextPct}%${C.reset}`;
  }

  /**
   * Set the context-window fill percentage (0–100), or null to hide it.
   * Driven by the runtime's `context_usage` KV, scaled to the compaction
   * trigger (see index.ts poll). Always painted on the status line far right.
   */
  setContextPct(pct: number | null): void {
    const next = pct == null ? null : Math.max(0, Math.min(100, Math.round(pct)));
    if (next === this.contextPct) return;
    this.contextPct = next;
    this.renderBottom();
  }

  /** Plain context label (no ANSI) for menu hints, or "" when unknown. */
  contextPctLabel(): string {
    return this.contextPct == null ? "" : `context ${this.contextPct}%`;
  }

  /** Register the slash commands shown in the inline `/` palette. */
  setCommands(commands: SlashCommand[]): void {
    this.commands = commands;
  }

  /** Is the `/` command palette currently showing? (input starts with "/".) */
  private paletteOpen(): boolean {
    return this.started && !this.takeoverHandler && this.commands.length > 0 && this.inputBuffer.startsWith("/");
  }

  /** Commands matching the text typed after "/", in declared order. */
  private filteredCommands(): SlashCommand[] {
    if (!this.inputBuffer.startsWith("/")) return [];
    const q = this.inputBuffer.slice(1).trim().toLowerCase();
    if (q === "") return this.commands;
    return this.commands.filter(
      (c) => c.name.startsWith(q) || c.name.includes(q) || c.label.toLowerCase().includes(q)
    );
  }

  private runCommand(cmd: SlashCommand): void {
    this.inputBuffer = "";
    this.cursorPos = 0;
    this.slashIdx = 0;
    this.renderBottom();
    void Promise.resolve(cmd.run()).catch(() => {});
  }

  /**
   * The summary line ABOVE the prompt. While working it reads
   * `⣷ Working <step> <elapsed> ↑in ↓out`; when idle it keeps the cumulative
   * token totals visible (`↑in ↓out`) so they live in the summary rather than
   * crowding the prompt. Null when idle with nothing counted yet.
   */
  private statusLineText(cols: number): string | null {
    const tk = this.tokensText();
    const gauge = this.contextGaugeText();
    // Reserve room on the right for the context gauge so it can sit flush
    // against the terminal edge without ever wrapping the row.
    const gaugeReserve = gauge ? this.visibleWidth(gauge) + 1 : 0;
    let line: string | null;
    if (this.working) {
      const el = this.formatElapsed(Date.now() - this.workingStart);
      const right = `${C.dim}${el}${C.reset}${tk ? " " + tk : ""}`;
      const head = `${this.spinnerFrame()} ${this.wordMill.text(Date.now())}`;
      const avail = Math.max(
        0,
        cols - this.visibleWidth(head) - this.visibleWidth(right) - 2 - gaugeReserve
      );
      let stepPart = "";
      if (this.step && avail > 1) {
        let s = this.step;
        if (s.length > avail) s = s.slice(0, avail - 1) + "…";
        stepPart = ` ${C.dim}${s}${C.reset}`;
      }
      line = `${head}${stepPart} ${right}`;
    } else if (this.goalComplete) {
      // Idle. Once a goal's every step is done we surface "Goal complete." here
      // (the goal area itself is cleared) until the next turn begins.
      const done = `${C.bold}${gradientText("✓ Goal complete.")}${C.reset}`;
      line = tk ? `${done} ${tk}` : done;
    } else {
      line = tk ? tk : null;
    }
    if (!gauge) return line;
    const base = line ?? "";
    const pad = Math.max(1, cols - 1 - this.visibleWidth(base) - this.visibleWidth(gauge));
    return `${base}${" ".repeat(pad)}${gauge}`;
  }

  /** The prompt line prefix (with ANSI colour) that precedes the typed text. */
  private promptPrefix(): string {
    // Queue/bg badges + level-tinted carrot. Permission level also appears in
    // the box top-border (far right); metrics live on the status line above.
    const q = this.queuedCount > 0 ? `${C.yellow}[⏳ ${this.queuedCount} queued]${C.reset} ` : "";
    // The bg badge renders inverted (reverse video) while selected via ←.
    const bgText = `[⚙ ${this.bgCount} bg]`;
    const bg =
      this.bgCount > 0
        ? this.bgBadgeSelected
          ? `${C.cyan}\x1b[7m${bgText}\x1b[27m${C.reset} `
          : `${C.cyan}${bgText}${C.reset} `
        : "";
    return `${q}${bg}${this.levelColor()}❯${C.reset} `;
  }

  private visibleWidth(s: string): number {
    // eslint-disable-next-line no-control-regex
    let w = 0;
    for (const ch of s.replace(/\x1b\[[0-9;]*m/g, "")) {
      w += isWideCodePoint(ch.codePointAt(0)!) ? 2 : 1;
    }
    return w;
  }

  /**
   * Truncate a styled string to at most `max` VISIBLE columns, copying ANSI
   * escape sequences through without counting them and appending a reset if it
   * cut mid-style. The redraw's whole height math assumes every HUD row is
   * exactly ONE physical terminal row — true only while each row's visible
   * width is strictly less than `cols`. A row of width == cols hits the
   * terminal's auto-wrap boundary: many terminals push the cursor to the next
   * line, so the following CR/LF yields an EXTRA physical row we didn't count,
   * and the region marches down the screen leaving a trail on every tick. Every
   * HUD row is clamped through here to keep that invariant no matter what any
   * individual line builder produces.
   */
  private clampVisible(s: string, max: number): string {
    let out = "";
    let width = 0;
    let i = 0;
    let truncated = false;
    while (i < s.length) {
      if (s[i] === "\x1b" && s[i + 1] === "[") {
        let j = i + 2;
        while (j < s.length && !/[a-zA-Z]/.test(s[j])) j++;
        out += s.slice(i, j + 1); // copy the full escape sequence verbatim
        i = j + 1;
        continue;
      }
      // Advance by code point, counting terminal COLUMNS (a CJK/emoji glyph
      // occupies two) so a wide character can't sneak the row past `max`.
      const cp = s.codePointAt(i)!;
      const chLen = cp > 0xffff ? 2 : 1;
      const chWidth = isWideCodePoint(cp) ? 2 : 1;
      if (width + chWidth > max) {
        truncated = true;
        break;
      }
      out += s.slice(i, i + chLen);
      width += chWidth;
      i += chLen;
    }
    if (truncated) out += C.reset;
    return out;
  }

  /**
   * The slash-palette block rendered BELOW the input. While the palette is open
   * we reserve a fixed number of rows — one per available command — padding with
   * blank rows so the region height never changes as the filter narrows. That
   * pins the input: the region scrolls into place once when the palette opens,
   * then nothing below the input resizes, so the text you're typing never jumps.
   * Returns `[]` when the palette is closed (no reservation, input sits at the
   * bottom as usual).
   */
  private paletteBlockLines(cols: number): string[] {
    if (!this.paletteOpen()) return [];
    const rows = this.paletteLines(cols).map((r) => this.clampVisible(r, Math.max(1, cols - 1)));
    const reserved = Math.max(this.commands.length, rows.length);
    while (rows.length < reserved) rows.push(""); // blank padding holds the height
    return rows;
  }

  /**
   * Build the slash-palette rows for the current filter. Each row is clamped to
   * ONE physical line (a wrapped row would desync the move-up redraw), with the
   * `/name` highlighted, the label dimmed, and the hint right-aligned.
   */
  private paletteLines(cols: number): string[] {
    if (!this.paletteOpen()) return [];
    const matches = this.filteredCommands();
    if (matches.length === 0) return [`  ${C.gray}no matching command${C.reset}`];
    const cur = Math.min(this.slashIdx, matches.length - 1);
    const pointerW = 2;
    return matches.map((cmd, i) => {
      const sel = i === cur;
      const hint = (typeof cmd.hint === "function" ? cmd.hint() : cmd.hint) ?? "";
      const hintW = hint.length;
      const name = `/${cmd.name}`;
      let visible = `${name}  ${cmd.label}`;
      const labelMax = Math.max(6, cols - pointerW - (hintW ? hintW + 2 : 0));
      if (visible.length > labelMax) visible = visible.slice(0, labelMax - 1) + "…";
      const desc = visible.slice(name.length); // "  <label…>" (or "…" if name was clipped)
      const pointer = sel ? `${C.magenta}❯${C.reset} ` : "  ";
      const nameStyled = sel ? `${C.bold}${C.cyan}${name}${C.reset}` : `${C.cyan}${name}${C.reset}`;
      let line = `${pointer}${nameStyled}${C.gray}${desc}${C.reset}`;
      if (hintW) {
        const gap = Math.max(2, cols - pointerW - visible.length - hintW);
        line += `${" ".repeat(gap)}${C.gray}${hint}${C.reset}`;
      }
      return line;
    });
  }

  /**
   * Physical rows a previously painted line of visible width `w` occupies after
   * the terminal rewraps it to `cols`. Hard-written HUD lines reflow this way.
   */
  private rewrapRows(w: number, cols: number): number {
    return Math.max(1, Math.ceil(Math.max(w, 1) / Math.max(1, cols)));
  }

  /**
   * Move the cursor to the top-left of the current bottom region.
   *
   * Same width as the last draw → the caret's recorded row offset is exact.
   * Width CHANGED (terminal resized) → previously drawn rows re-wrapped, so
   * that offset is stale. Recompute using every stored region row width under
   * the NEW wrap: rows above the caret row + (caret row's rewrap − 1) so we
   * prefer a slight over-move (clean wipe) over under-move (ghost chrome).
   */
  private moveToRegionTop(): void {
    process.stdout.write("\r");
    if (!this.bottomDrawn) return;
    const cols = process.stdout.columns || 80;
    let up: number;
    if (cols !== this.lastDrawnCols && this.lastDrawnCols > 0 && this.drawnRegionWidths.length) {
      // Prefer the full-region width list; fall back to the legacy above-body list.
      const widths =
        this.drawnRegionWidths.length > 0 ? this.drawnRegionWidths : this.drawnHudWidths;
      const caretIdx = Math.max(
        0,
        Math.min(this.lastCaretRegionIndex, Math.max(0, widths.length - 1))
      );
      let above = 0;
      for (let i = 0; i < caretIdx; i++) above += this.rewrapRows(widths[i], cols);
      // Caret row may itself soft-wrap under the new width. Terminals usually
      // keep the cursor on the last physical segment when the line grew longer
      // than the new width at the caret column — use (rewrap − 1) so we never
      // under-move and leave a ghost top border above the clear.
      const caretPhysical = this.rewrapRows(widths[caretIdx] ?? 1, cols);
      up = above + Math.max(0, caretPhysical - 1);
      // Safety pad on resize only: one extra row absorbs off-by-one rewrap
      // disagreements across terminals (some count the wrap boundary differently).
      up += 1;
    } else {
      up = this.lastCursorRow;
    }
    if (up > 0) process.stdout.write(`\x1b[${up}A`);
  }

  /**
   * Render the bottom region: status + subagents above a side-margined expanding
   * input box (level in the top border far right), with the caret on the input
   * body. Uses only relative cursor moves so it survives terminal scrolling when
   * the region grows near the bottom of the screen.
   *
   * Layout (top → bottom):
   *   preview · ruler · notice · quit · status · subagents · goal ·
   *   box top (level) · box body · box bottom · palette
   */
  private renderBottom(): void {
    if (!this.started || this.takeoverHandler) return; // not yet active, or a takeover owns the bottom
    // Mid-drag: terminal width is in flux — don't paint until the debounced
    // resize handler fires one authoritative clear+repaint.
    if (this.resizePending) return;
    const cols = process.stdout.columns || 80;

    this.moveToRegionTop(); // position cursor at the old region's top

    // Visible width of every HUD row this draw (full region). Resize path uses
    // the full list so body / bottom border / palette rewraps are counted too.
    const hudWidths: number[] = [];
    // Buffer every row into one string and flush in a single write() —
    // flushing each row separately makes the redraw flicker on every tick.
    const hudRows: string[] = [];
    // Clamp EVERY row to one column short of the terminal width so it can never
    // hit the auto-wrap boundary and silently consume a second physical row
    // (see clampVisible). This is what keeps the region-height math exact.
    const rowCap = Math.max(1, cols - 1);
    const writeHudRow = (line: string) => {
      // Sanitize BEFORE clamping: an embedded newline or tab (a garbled step
      // label, a multi-line subagent status) would add physical rows/columns
      // the region math doesn't count, and the redraw would then stamp the
      // region's top rows into the scrollback on every tick.
      const row = this.clampVisible(sanitizeHudRow(line), rowCap);
      hudWidths.push(this.visibleWidth(row));
      hudRows.push(row);
    };

    // ── output zone (above the work-area ruler) ──────────────────────────────
    // Live streaming preview (reasoning dim-italic, then the answer plain),
    // bounded to a tail. This is model OUTPUT, so it belongs with the transcript
    // ABOVE the ruler — never interleaved between the status and subagent lines.
    // Cleared on commit.
    const previewLines = this.streamPreviewLines(cols);
    for (const line of previewLines) writeHudRow(line);

    // ── work area (the pinned HUD, below a full-width ruler) ──────────────────
    // The ruler is the TOP line of the work area: a full-width divider ALWAYS
    // drawn between the plain output above and the constantly-redrawn run area
    // below. It's part of the redrawn region (not printed into the scrollback).
    writeHudRow(`${C.dim}${"─".repeat(cols)}${C.reset}`);

    // Work-area left pad matches the input box outer edge (not the inner
    // content). Status, subagents, and goal share this inset with the box.
    const geo = inputBoxGeometry(cols);
    const workPad = " ".repeat(geo.margin);
    // Width available for status/subagent content after the shared left inset.
    const workCols = Math.max(8, cols - geo.margin);

    // Transient disconnect notice (one row).
    const noticeLine = this.connected
      ? null
      : `${C.yellow}⚠ lost connection to the workspace — reconnecting…${C.reset}`;
    if (noticeLine) writeHudRow(workPad + noticeLine);

    // Transient "press again to exit" hint (one row).
    const quitLine = this.quitArmed ? `${C.dim}Press Control-C again to exit${C.reset}` : null;
    if (quitLine) writeHudRow(workPad + quitLine);

    // Status line FIRST (working spinner / tokens / goal-complete) — sits with
    // the subagents directly above the input box, same left pad as the box.
    const statusLine = this.statusLineText(workCols);
    if (statusLine) writeHudRow(workPad + statusLine);

    // Subagent lines (one per active subagent), just under the status line.
    // Each subagent gets its own hue (loader + label).
    const frame = FRAMES[Math.floor(Date.now() / SPINNER_MS) % FRAMES.length];
    for (const sub of this.subagents) {
      const color =
        sub.agentName === COMPACTION_AGENT
          ? COMPACTION_COLOR
          : SUBAGENT_COLORS[this.subagentColorByID.get(sub.id) ?? 0];
      // Clamp the label so the whole line stays within ONE physical row.
      const budget = workCols - 10;
      let label = sub.label;
      if (budget < 1) label = "";
      else if (label.length > budget) label = label.slice(0, Math.max(0, budget - 1)) + "…";
      const line = `${color}${frame}${C.reset} ${color}${label}${C.reset} ${C.dim}working${C.reset}`;
      writeHudRow(workPad + line);
    }

    // Live goal checklist, just below status/subagents: header + indented todos.
    const goalLines = this.goalLines(workCols);
    for (const line of goalLines) writeHudRow(workPad + line);

    // ── boxed input (side margins, expanding body, level in top-right border) ─
    const prefix = this.promptPrefix();
    const pw = this.visibleWidth(prefix);
    // Rotating greyscale border only while the MAIN agent is working (status
    // spinner showing). Subagent-only activity keeps a static grey rim so the
    // highlight always means "the primary turn is in flight".
    const borderAnimating = this.working;
    // Full lap ~1.4s; phase is head position 0..1 along the perimeter.
    const borderPhase = borderAnimating ? (Date.now() % 1400) / 1400 : null;
    const boxRows = buildInputBoxRows({
      cols,
      inputBuffer: this.inputBuffer,
      prefix,
      prefixWidth: pw,
      level: this.level,
      levelColor: this.levelColor(),
      borderColor: inputBoxBorderColor(),
      borderPhase,
    });
    // boxRows = [top, ...body, bottom]
    const boxTop = boxRows[0];
    const boxBottom = boxRows[boxRows.length - 1];
    const boxBody = boxRows.slice(1, -1);
    writeHudRow(boxTop);

    const bodyStartIdx = hudWidths.length; // first body row index in the region
    for (const row of boxBody) writeHudRow(row);
    writeHudRow(boxBottom);

    // Inline slash-command palette BELOW the box (input never shifts as it
    // filters). Fixed reserved height while open so the box stays pinned.
    const paletteBlock = this.paletteBlockLines(cols);
    for (const line of paletteBlock) writeHudRow(line);

    // Caret: place on the matching body row at content column + left pad.
    const layout = this.inputLayout();
    const bodyRowCount = Math.max(1, boxBody.length);
    const caretBodyRow = Math.max(0, Math.min(layout.caretRow, bodyRowCount - 1));
    const caretRegionRow = bodyStartIdx + caretBodyRow;
    const caretScreenCol = Math.min(rowCap - 1, geo.leftPad + layout.caretCol);

    this.lastCursorRow = caretRegionRow;
    this.lastCaretRegionIndex = caretRegionRow;
    // Full region widths — resize rewrap must count body, bottom, and palette.
    this.drawnRegionWidths = hudWidths.slice();
    this.drawnHudWidths = hudWidths.slice(0, bodyStartIdx); // legacy above-body slice
    this.lastDrawnCols = cols;
    this.bottomDrawn = true;

    // ── single flush ────────────────────────────────────────────────────────
    // One write() for the entire region + caret reposition. Always clear from
    // the region top first (`\x1b[J`) so a resize never leaves ghost chrome
    // below/around the new paint. On resize we already over-moved slightly in
    // moveToRegionTop so the clear reaches any rewrapped leftover rows.
    const totalRows = hudRows.length;
    const up = Math.max(0, totalRows - 1 - caretRegionRow);
    let out = "\x1b[J" + hudRows.join("\r\n");
    if (totalRows > 0) {
      out += "\r";
      if (up > 0) out += `\x1b[${up}A`;
      if (caretScreenCol > 0) out += `\x1b[${caretScreenCol}C`;
    }
    process.stdout.write(out);
  }

  private clearBottom(): void {
    if (!this.bottomDrawn) return;
    this.moveToRegionTop();
    process.stdout.write("\x1b[J");
    this.bottomDrawn = false;
  }

  // ── live streaming preview ────────────────────────────────────────────────
  // An ephemeral tail of the model's output above the status line: reasoning in
  // dim italic until the answer begins, then the answer plain. clearStream()
  // wipes it right before the finished message is committed to the transcript
  // (which renders full markdown), so there's no double-render.

  private static readonly STREAM_TAIL = 20;
  // How long a preview may sit untouched before it's wiped. The model often
  // reasons and then calls a tool without ever emitting an answer, so without
  // this the reasoning tail would linger on screen until the *next* thought (or
  // message) arrives. Re-armed on every delta → fires this long after the last.
  private static readonly STREAM_IDLE_MS = 10_000;

  /** Append a fragment of streamed answer text (rendered plain). */
  streamResponseDelta(delta: string, messageId?: string): void {
    this.beginStreamMessage(messageId);
    this.streamResponse += delta;
    this.scheduleStreamRedraw();
    this.armStreamIdleExpiry();
  }

  /**
   * Append a fragment of streamed internal reasoning (rendered dim italic).
   * Ignored once the answer has started, since reasoning precedes the answer.
   */
  streamThinkingDelta(delta: string, messageId?: string): void {
    this.beginStreamMessage(messageId);
    if (this.streamResponse) return;
    this.streamThinking += delta;
    this.scheduleStreamRedraw();
    this.armStreamIdleExpiry();
  }

  /** Reset the preview when a new message starts, so two messages' output (e.g.
   *  one that only reasons then calls a tool, then the next) never blend. */
  private beginStreamMessage(messageId?: string): void {
    if (messageId !== undefined && messageId !== this.streamMessageId) {
      this.streamMessageId = messageId;
      this.streamThinking = "";
      this.streamResponse = "";
    }
  }

  /** Wipe the live preview — call right before committing the final message. */
  clearStream(): void {
    if (this.streamRedrawTimer) {
      clearTimeout(this.streamRedrawTimer);
      this.streamRedrawTimer = null;
    }
    if (this.streamIdleTimer) {
      clearTimeout(this.streamIdleTimer);
      this.streamIdleTimer = null;
    }
    this.streamMessageId = null;
    if (!this.streamThinking && !this.streamResponse) return;
    this.streamThinking = "";
    this.streamResponse = "";
    this.renderBottom();
  }

  private scheduleStreamRedraw(): void {
    if (this.streamRedrawTimer || this.takeoverHandler || !this.started) return;
    // Batch rapid deltas into ~25fps redraws so streaming stays smooth.
    this.streamRedrawTimer = setTimeout(() => {
      this.streamRedrawTimer = null;
      this.renderBottom();
    }, 40);
  }

  /** Re-armed on every streamed delta: once the model goes quiet for a beat, the
   *  preview is stale, so wipe it instead of letting it sit until the next
   *  message. The committed message (if any) still renders in full via
   *  clearStream(), so nothing is lost. */
  private armStreamIdleExpiry(): void {
    if (this.streamIdleTimer) clearTimeout(this.streamIdleTimer);
    this.streamIdleTimer = setTimeout(() => {
      this.streamIdleTimer = null;
      if (!this.streamThinking && !this.streamResponse) return;
      this.streamThinking = "";
      this.streamResponse = "";
      this.renderBottom();
    }, Tui.STREAM_IDLE_MS);
  }

  /**
   * The preview's physical rows: a tail of reasoning (dim italic) before the
   * answer starts, otherwise a tail of the answer (plain). Each row is clamped to
   * one terminal line so the bottom-region redraw math stays correct.
   *
   * Rows match the committed transcript formatting so a finished message doesn't
   * visibly "snap" into shape: the answer's first line carries the grey gutter
   * dot (as long as it hasn't scrolled out of the tail) and the body indents two
   * spaces beneath it; reasoning aligns at the same indent, dotless.
   */
  private streamPreviewLines(cols: number): string[] {
    // Reasoning gets a distinct, low-contrast voice: italic + a dark grey that
    // recedes well behind the plain (default-color) answer text.
    const thinkStyle = "\x1b[3m\x1b[38;5;240m";
    // 2-col gutter lead + clamp to one physical row.
    const clamp = (s: string, wrap: string, lead: string): string => {
      const max = cols - 2;
      const t = s.length > max ? s.slice(0, Math.max(0, max - 1)) + "…" : s;
      return wrap ? `${lead}${wrap}${t}${C.reset}` : `${lead}${t}`;
    };
    // Drop blank lines — streamed leading/internal newlines (e.g. a leading
    // "\n\n") must not render as a pile of empty rows. Also drop lines of
    // leaked tool-call serialization (DSML tags / stray session markers a
    // model emits as text when its tool call fails to parse upstream): the
    // backend discards them from the committed message, so the live preview
    // must not flash them either.
    const leakedMarkup = /<\/?[|｜]DSML[|｜]|^\s*\[\/?SESSION\]\s*<?\s*$/;
    const realLines = (text: string): string[] =>
      text
        .replace(/\r/g, "")
        .split("\n")
        .filter((l) => l.trim() !== "" && !leakedMarkup.test(l));
    if (this.streamResponse) {
      const all = realLines(this.streamResponse);
      const shown = all.slice(-Tui.STREAM_TAIL);
      const firstVisible = all.length <= Tui.STREAM_TAIL; // response's first line still in the tail
      return shown.map((l, i) =>
        clamp(l, "", i === 0 && firstVisible ? `${C.gray}•${C.reset} ` : "  ")
      );
    }
    if (this.streamThinking) {
      return realLines(this.streamThinking)
        .slice(-Tui.STREAM_TAIL)
        .map((l) => clamp(l, thinkStyle, "  "));
    }
    return [];
  }

  // ── live goal checklist ───────────────────────────────────────────────────

  /**
   * Update the goal from the goal_updated event (or the initial fetch). When
   * every step is done, the goal is fully achieved: we clear the goal area and
   * flip on the "Goal complete." status badge instead of leaving a finished
   * checklist sitting there.
   */
  setGoal(goal: GoalData | null): void {
    const steps = goal?.steps;
    if (steps && Array.isArray(steps) && steps.length) {
      const allDone = steps.every((s) => s.status === "done");
      if (allDone) {
        this.goal = null;
        this.goalComplete = true;
      } else {
        this.goal = goal;
        this.goalComplete = false;
      }
    } else {
      this.goal = null;
    }
    this.renderBottom();
  }

  /**
   * The goal's physical rows: a header (the short summary + progress) then one
   * row per step, indented two spaces beneath it so the todos clearly belong to
   * the goal — ○ pending / ▸ in-progress / ✓ done — each clamped to a line.
   */
  private goalLines(cols: number): string[] {
    const steps = this.goal?.steps;
    if (!steps?.length) return [];
    const oneLine = (s: string) => s.replace(/\s+/g, " ").trim();
    const clamp = (s: string, max: number) =>
      s.length > max ? s.slice(0, Math.max(0, max - 1)) + "…" : s;
    const calmGreen = "\x1b[38;5;65m"; // muted, low-contrast done check
    const out: string[] = [];
    const done = steps.filter((s) => s.status === "done").length;
    const summary = oneLine(this.goal?.summary || this.goal?.description || "Goal");
    // Clamp the visible summary (not the ANSI-laden head) so we never cut an
    // escape sequence. Prefix is "Goal N/M " in visible characters.
    const prefix = `Goal ${done}/${steps.length} `;
    const sum = clamp(summary, Math.max(1, cols - prefix.length));
    out.push(`${C.bold}Goal${C.reset} ${C.gray}${done}/${steps.length}${C.reset} ${sum}`);
    for (const s of steps) {
      // Two-space indent under the goal, then the marker + step text.
      const text = clamp(oneLine(s.step || ""), Math.max(1, cols - 4));
      if (s.status === "done") out.push(`  ${calmGreen}✓${C.reset} ${C.dim}${text}${C.reset}`);
      else if (s.status === "in_progress") out.push(`  ${C.cyan}▸${C.reset} ${text}`);
      else out.push(`  ${C.gray}○ ${text}${C.reset}`);
    }
    return out;
  }

  /**
   * Transcript gutter: content starts at column 1, so the far-left column is a
   * clean strip where only status glyphs (✓ ✗ ⛔ …) land — scanning down the
   * left edge reads as a ledger of what happened. Lines already led by a
   * gutter glyph or whitespace (indented blocks, the user-message bar) pass
   * through untouched.
   */
  private gutterize(text: string): string {
    return text
      .split("\n")
      .map((line) => {
        const plain = line.replace(/\x1b\[[0-9;]*m/g, "");
        if (plain === "" || /^[\s✓✗⛔⚠⚙⚡⏳↪›❯◇─•]/.test(plain)) return line;
        return " " + line;
      })
      .join("\n");
  }

  /** Print a line of transcript above the persistent input. */
  print(text: string): void {
    const line = this.gutterize(text);
    if (this.takeoverHandler) {
      this.bufferedPrints.push(line);
      return;
    }
    this.clearBottom();
    process.stdout.write(line + "\n");
    this.renderBottom();
  }

  /** Multi-line convenience. */
  printLines(lines: string[]): void {
    for (const l of lines) this.print(l);
  }

  /**
   * Print a user message as a highlighted block so it stands out in the
   * transcript (à la Codex). The bar is sized to the text (not full width, which
   * would wrap awkwardly), padded with a space on each side and a blank line
   * above and below, and a teal `›` marks the first row.
   */
  printUserMessage(text: string): void {
    const cols = Math.max(20, process.stdout.columns || 80);
    const bg = "\x1b[48;5;238m"; // dark-gray background bar (visible against the terminal)
    const limit = Math.max(8, cols - 6); // wrap width, leaving room for padding
    // Word-wrap ONE logical line to `limit`; returns [""] for a blank line so a
    // paragraph separator survives as a blank row in the bar.
    const wrapLine = (logical: string): string[] => {
      const words = logical.replace(/[^\S\n]+/g, " ").trim().split(" ").filter(Boolean);
      if (!words.length) return [""];
      const out: string[] = [];
      let cur = "";
      for (let w of words) {
        while (w.length > limit) {
          if (cur) {
            out.push(cur);
            cur = "";
          }
          out.push(w.slice(0, limit));
          w = w.slice(limit);
        }
        if (!cur) cur = w;
        else if (cur.length + 1 + w.length <= limit) cur += " " + w;
        else {
          out.push(cur);
          cur = w;
        }
      }
      if (cur || !out.length) out.push(cur);
      return out;
    };
    // Preserve the user's own line breaks (multi-line prompts, blank-line
    // paragraph separators) instead of collapsing everything into one block;
    // wrap each line independently to the bar width.
    const lines = text.replace(/\r\n?/g, "\n").split("\n").flatMap(wrapLine);
    // Width the bar to the longest line + the 2-col marker/indent.
    const innerW = 2 + Math.max(1, ...lines.map((l) => l.length));
    this.print(""); // padding above
    lines.forEach((line, i) => {
      const rowText = (i === 0 ? "❯ " : "  ") + line;
      const padded = rowText.padEnd(innerW);
      // recolor the leading marker; one space of padding inside the bar each side
      const inner = i === 0 ? `${C.teal}❯${C.reset}${bg}${padded.slice(1)}` : padded;
      this.print(`${bg} ${inner} ${C.reset}`);
    });
    this.print(""); // padding below
  }

  // ─── working indicator (turn state) ───────────────────────────────────────

  setWorking(on: boolean): void {
    if (on && !this.working) {
      this.working = true;
      this.workingStart = Date.now();
      // Every turn's label starts back at "Working" before the word morphs.
      this.wordMill.reset(this.workingStart);
      // A new turn supersedes the previous "Goal complete." badge.
      this.goalComplete = false;
    } else if (!on) {
      this.working = false;
    }
    this.syncSpinner();
    this.renderBottom();
  }

  /** The subagents currently working, one persistent line each. Each keeps a
   *  stable, distinct colour for as long as it's active; the compaction agent
   *  is always orange (its colour never comes from the shared pool). */
  setSubagents(subagents: { id: string; label: string; agentName?: string }[]): void {
    this.subagents = subagents;
    // Release colours held by subagents that have finished.
    const active = new Set(subagents.map((s) => s.id));
    for (const id of [...this.subagentColorByID.keys()]) {
      if (!active.has(id)) this.subagentColorByID.delete(id);
    }
    // Give each newcomer the lowest colour index not currently in use, so
    // concurrent subagents never share a hue (until the palette is exhausted).
    for (const s of subagents) {
      if (s.agentName === COMPACTION_AGENT) continue; // fixed orange, not pooled
      if (this.subagentColorByID.has(s.id)) continue;
      const used = new Set(this.subagentColorByID.values());
      let idx = 0;
      while (used.has(idx) && idx < SUBAGENT_COLORS.length - 1) idx++;
      this.subagentColorByID.set(s.id, idx);
    }
    this.syncSpinner();
    this.renderBottom();
  }

  /** Run the spinner animation while anything (the agent or a subagent) is active. */
  private syncSpinner(): void {
    const spinning = this.working || this.subagents.length > 0;
    if (spinning && !this.spinnerTimer) {
      this.spinnerTimer = setInterval(() => this.renderBottom(), SPINNER_MS);
    } else if (!spinning && this.spinnerTimer) {
      clearInterval(this.spinnerTimer);
      this.spinnerTimer = null;
    }
  }

  get isWorking(): boolean {
    return this.working;
  }

  setBackgroundCount(n: number): void {
    this.bgCount = n;
    if (n === 0) this.bgBadgeSelected = false; // badge gone → nothing to select
    this.renderBottom();
  }
  setQueuedCount(n: number): void {
    this.queuedCount = n;
    this.renderBottom();
  }
  setConnected(connected: boolean): void {
    this.connected = connected;
    this.renderBottom();
  }

  // ─── input buffer access (for up-arrow editing of queued messages) ─────────

  getInput(): string {
    return this.inputBuffer;
  }
  /** Replace the input (and any pasted images tied to placeholders in it). */
  setInput(text: string, images: PastedImage[] = []): void {
    this.inputBuffer = text;
    this.cursorPos = text.length;
    this.pendingImages = images;
    this.historyIdx = null;
    this.renderBottom();
  }

  /** Cumulative token totals shown on the prompt line (`outTokens` includes live). */
  setTokens(inTokens: number, outTokens: number): void {
    this.tokensIn = inTokens;
    this.tokensOut = outTokens;
    this.renderBottom();
  }

  /**
   * Current step the agent is working on, shown on the line above the prompt.
   * `outTokens` is the output produced during this step. The step's own timer
   * resets whenever the label changes.
   */
  setStep(label: string | null, outTokens: number): void {
    // Collapse to one line: the label comes from server-sent tool progress
    // text, which can be multi-line garbage when an upstream parse goes wrong.
    const next = label && label.trim() ? label.replace(/\s+/g, " ").trim() : null;
    if (next !== this.step) {
      this.step = next;
      this.stepStart = Date.now();
    }
    this.stepOut = outTokens;
    this.renderBottom();
  }

  // ─── takeover helpers (approval / menus) ───────────────────────────────────

  private beginTakeover(): void {
    this.bgBadgeSelected = false; // a menu/prompt owns the bottom now
    this.clearBottom();
    process.stdout.write("\r\x1b[K");
    process.stdout.write("\x1b[?25l"); // hide the text cursor while a menu/prompt owns the screen
    this.bottomDrawn = false;
  }

  private endTakeover(): void {
    this.takeoverHandler = null;
    process.stdout.write("\x1b[?25h"); // restore the cursor
    const buffered = this.bufferedPrints;
    this.bufferedPrints = [];
    for (const t of buffered) {
      process.stdout.write(t + "\n");
    }
    this.renderBottom();
  }

  /**
   * ## Boxed prompt — reusable HUD free-text component
   *
   * Single-line free-text capture using the **same chrome as the main input
   * box** (side margin, grey rounded rim, level-tinted `❯`, real caret). A
   * left-aligned label sits in the top border rim instead of the permission
   * level dots.
   *
   * Prefer this over {@link Tui.prompt} whenever the capture should feel like
   * part of the HUD rather than a legacy bar — denial feedback, handoff notes,
   * reject-with-reason, rename, or any other short free-text moment.
   *
   * ```
   * ╭ Denial feedback ────────────────────────╮
   * │ ❯ the user-typed note                    │
   * ╰──────────────────────────────────────────╯
   * ```
   *
   * **Keys**
   * - Enter — submit (resolves the trimmed string; may be `""`)
   * - Esc — clear a non-empty draft; Esc again (empty) cancels → `null`
   *   (override with `escClearsFirst: false` to always cancel)
   *
   * **Nested mode** (`nested: true`) — call from inside another takeover
   * (e.g. approval → feedback) so this component only steals the key handler
   * and paints/erases its own three rows; the parent still owns begin/end.
   *
   * Do **not** use brand-gradient text for the label — gradient is reserved
   * for success moments (`✓ Goal complete.`, farewell, etc.).
   *
   * @returns trimmed text on Enter, or `null` if the user cancelled with Esc
   */
  boxedPrompt(opts: BoxedPromptOptions): Promise<string | null> {
    return new Promise((resolve) => {
      const nested = !!opts.nested;
      const escClearsFirst = opts.escClearsFirst !== false;
      const borderLabel = opts.borderLabel || "Feedback";
      const labelColor = opts.labelColor ?? "\x1b[90m";
      let buf = opts.initial ?? "";

      if (!nested) this.beginTakeover();
      process.stdout.write("\x1b[?25h"); // real caret, same as main input

      const cols = process.stdout.columns || 80;
      const geo = inputBoxGeometry(cols);
      const border = inputBoxBorderColor();
      const pad = " ".repeat(geo.margin);
      const rowCap = Math.max(1, cols - 1);
      const reset = C.reset;
      const prompt = `${this.levelColor()}❯${reset} `;
      const promptW = 2; // "❯ "

      const writeRow = (line: string) => {
        const row = this.clampVisible(sanitizeHudRow(line), rowCap);
        process.stdout.write(`\r\x1b[K${row}\n`);
      };

      // Frame is 3 rows (top / body / bottom). After three writeRow newlines
      // the cursor sits one row BELOW the bottom, so: 2A → body, 3A → top.
      const draw = () => {
        writeRow(pad + buildBoxTopLeftLabel(geo.boxW, borderLabel, border, labelColor));
        writeRow(pad + buildBoxBody(prompt + buf, geo.contentW, border));
        writeRow(pad + buildBoxBottom(geo.boxW, border));
        // Park the real caret after the typed text on the body row.
        process.stdout.write("\r\x1b[2A");
        const col = geo.leftPad + promptW + buf.length;
        if (col > 0) process.stdout.write(`\x1b[${Math.min(rowCap - 1, col)}C`);
      };

      const clearFrame = () => {
        // Caret is on the body row → step to the top, then clear down.
        process.stdout.write("\r\x1b[1A\x1b[J");
      };

      const redraw = () => {
        // Caret is on body; step to top, then repaint the whole frame.
        process.stdout.write("\r\x1b[1A");
        draw();
      };

      const finish = (value: string | null) => {
        clearFrame();
        if (!nested) this.endTakeover();
        else this.takeoverHandler = null;
        resolve(value);
      };

      draw();
      this.takeoverHandler = (str, key) => {
        if (key?.name === "escape") {
          if (escClearsFirst && buf.length > 0) {
            buf = "";
            redraw();
            return;
          }
          finish(null);
          return;
        }
        if (key?.name === "return" || key?.name === "enter") {
          finish(buf.trim());
          return;
        }
        if (key?.name === "backspace") {
          if (!buf.length) return;
          buf = buf.slice(0, -1);
          redraw();
          return;
        }
        if (str && !key?.ctrl && !key?.meta && str >= " ") {
          if (buf.length < Math.max(8, geo.contentW - promptW - 1)) {
            buf += str;
            redraw();
          }
        }
      };
    });
  }

  /**
   * Permission prompt: boxed chrome matching the HUD, arrow-navigable options
   * with shortcuts. Includes "Deny with feedback" — choosing it (or Tab)
   * opens {@link Tui.boxedPrompt} so the reason can ride back to the agent.
   */
  approval(
    question: string,
    risk: number
  ): Promise<{ choice: "allow" | "deny" | "always" | "always_risk"; reason?: string }> {
    return new Promise((resolve) => {
      type Choice = "allow" | "always" | "always_risk" | "deny" | "deny_feedback";
      const options: { value: Choice; label: string; shortcut: string; color: string; hint?: string }[] = [
        { value: "allow", label: "Allow once", shortcut: "y", color: C.green },
        { value: "always", label: "Always allow this tool", shortcut: "a", color: C.cyan },
        {
          value: "always_risk",
          label: `Allow level ${risk} and below this session`,
          shortcut: "l",
          color: C.cyan,
        },
        { value: "deny", label: "Deny", shortcut: "n", color: C.red },
        {
          value: "deny_feedback",
          label: "Deny with feedback",
          shortcut: "d",
          color: C.magenta,
          hint: "tell the agent why",
        },
      ];
      let idx = 0;
      const riskLevel = Math.max(1, Math.min(5, Math.round(risk))) as Level;
      // Filled dots in warm risk colour, empty dull.
      const riskBar = borderLevelDotsStyled(riskLevel, C.yellow, "\x1b[38;5;240m");

      this.beginTakeover();
      const cols = process.stdout.columns || 80;
      const geo = inputBoxGeometry(cols);
      const border = inputBoxBorderColor();
      const pad = " ".repeat(geo.margin);
      const rowCap = Math.max(1, cols - 1);
      const reset = C.reset;

      // Clamp a line of content to the box interior; hard-wrap long summaries.
      const wrapContent = (text: string): string[] => {
        const flat = text.replace(/\s+/g, " ").trim();
        if (!flat) return [""];
        const out: string[] = [];
        let rest = flat;
        while (rest.length > geo.contentW) {
          out.push(rest.slice(0, geo.contentW - 1) + "…");
          rest = rest.slice(geo.contentW - 1);
          if (out.length >= 4) {
            // Cap summary height so a huge command doesn't flood the dialog.
            break;
          }
        }
        if (out.length < 4) out.push(rest);
        return out.length ? out : [""];
      };

      // Question may include a "why:" line from the agent — keep each logical
      // line, wrap each, strip any caller ANSI so the box paints cleanly.
      const summaryLines = question.split("\n").flatMap((line) => {
        // eslint-disable-next-line no-control-regex
        const plain = line.replace(/\x1b\[[0-9;]*m/g, "").trim();
        if (!plain) return [];
        const isWhy = /^why:/i.test(plain);
        return wrapContent(plain).map((l) => (isWhy ? `${C.dim}${l}${reset}` : l));
      });
      if (!summaryLines.length) summaryLines.push(`${C.dim}(no details)${reset}`);

      const writeRow = (line: string) => {
        const row = this.clampVisible(sanitizeHudRow(line), rowCap);
        process.stdout.write(`\r\x1b[K${row}\n`);
      };

      // Full frame height: blank + title + top + summary… + blank + options… + bottom
      const optionCount = options.length;
      const summaryCount = summaryLines.length;
      const boxRows = 1 /*top*/ + summaryCount + 1 /*blank*/ + optionCount + 1 /*bottom*/;
      const titleRows = 2; // blank + title
      const totalRows = titleRows + boxRows;

      const renderOptionContent = (i: number): string => {
        const o = options[i];
        const sel = i === idx;
        const pointer = sel ? `${o.color}❯${reset} ` : "  ";
        const label = sel ? `${C.bold}${o.color}${o.label}${reset}` : `${C.dim}${o.label}${reset}`;
        const shortcut = `${C.gray}(${o.shortcut})${reset}`;
        const hint = o.hint && sel ? `${C.dim}  ${o.hint}${reset}` : "";
        // pointer(2) + label + " " + (x) + hint
        const plainLabel = o.label;
        const used = 2 + plainLabel.length + 1 + 3 + (o.hint && sel ? 2 + o.hint.length : 0);
        const gap = Math.max(1, geo.contentW - used);
        return `${pointer}${label}${" ".repeat(gap)}${shortcut}${hint}`;
      };

      const drawFrame = (moveUp: boolean) => {
        if (moveUp) process.stdout.write(`\x1b[${totalRows}A`);
        // Title above the box — plain bold (gradient is reserved for success).
        writeRow("");
        writeRow(
          `${pad}${C.bold}Permission needed${reset}  ${C.dim}risk${reset} ${riskBar}`
        );
        writeRow(pad + buildBoxTopPlain(geo.boxW, border));
        for (const line of summaryLines) {
          writeRow(pad + buildBoxBody(line, geo.contentW, border));
        }
        // Spacer row inside the box
        writeRow(pad + buildBoxBody("", geo.contentW, border));
        for (let i = 0; i < optionCount; i++) {
          writeRow(pad + buildBoxBody(renderOptionContent(i), geo.contentW, border));
        }
        writeRow(pad + buildBoxBottom(geo.boxW, border));
      };

      drawFrame(false);

      const erase = () => {
        process.stdout.write("\r");
        if (totalRows > 0) process.stdout.write(`\x1b[${totalRows}A`);
        process.stdout.write("\x1b[J");
      };

      const done = (choice: "allow" | "deny" | "always" | "always_risk", reason?: string) => {
        erase();
        this.endTakeover();
        resolve(reason !== undefined ? { choice, reason } : { choice });
      };

      // Feedback reuses the boxed-prompt component (same chrome as main input,
      // left rim label). Nested so we keep ownership of begin/end takeover.
      const collectFeedback = () => {
        erase();
        void this.boxedPrompt({ borderLabel: "Denial feedback", nested: true }).then((why) => {
          this.endTakeover();
          if (why) resolve({ choice: "deny", reason: why });
          else resolve({ choice: "deny" });
        });
      };

      const pick = (value: Choice) => {
        if (value === "deny_feedback") collectFeedback();
        else done(value);
      };

      this.takeoverHandler = (str, key) => {
        if (key?.name === "up" || str === "k") {
          idx = (idx - 1 + optionCount) % optionCount;
          drawFrame(true);
        } else if (key?.name === "down" || str === "j") {
          idx = (idx + 1) % optionCount;
          drawFrame(true);
        } else if (key?.name === "tab") {
          // Tab = deny with feedback (legacy + discoverable).
          pick("deny_feedback");
        } else if (key?.name === "return" || key?.name === "enter") {
          pick(options[idx].value);
        } else if (key?.name === "escape") {
          done("deny");
        } else {
          const k = (str || "").toLowerCase();
          if (k === "y") pick("allow");
          else if (k === "a") pick("always");
          else if (k === "l") pick("always_risk");
          else if (k === "n") pick("deny");
          else if (k === "d" || k === "f") pick("deny_feedback");
        }
      };
    });
  }

  /**
   * Arrow-key selection menu (slash menu, process menu, resume). Pauses input.
   * Boxed chrome matches the bottom input HUD: side margin + grey rounded
   * border, selected row with a brand-tinted pointer.
   */
  select<T>(title: string, items: { label: string; hint?: string; value: T }[]): Promise<T | undefined> {
    return new Promise((resolve) => {
      let idx = 0;
      this.beginTakeover();
      const cols = process.stdout.columns || 80;
      const geo = inputBoxGeometry(cols);
      const border = inputBoxBorderColor();
      const pad = " ".repeat(geo.margin);
      const rowCap = Math.max(1, cols - 1);
      // Title sits above the box (same left edge as the border).
      // Layout: blank · title · top · items… · bottom  → cursor below bottom.
      if (title) process.stdout.write(`\n${pad}${title}\n`);
      else process.stdout.write("\n");

      // Each item is ONE physical row inside the box (preview left, hint right)
      // so a long session preview can't wrap and desync the move-up redraw.
      const renderItemContent = (i: number): string => {
        const it = items[i];
        const sel = i === idx;
        const hint = it.hint ?? "";
        const hintW = hint.length;
        const pointerW = 2; // "❯ " or "  "
        const labelMax = Math.max(4, geo.contentW - pointerW - (hintW ? hintW + 2 : 0));
        let label = it.label.replace(/\s+/g, " ").trim();
        if (label.length > labelMax) label = label.slice(0, Math.max(0, labelMax - 1)) + "…";
        const pointer = sel ? `${C.magenta}❯${C.reset} ` : "  ";
        const styledLabel = sel ? `${C.bold}${C.cyan}${label}${C.reset}` : `${C.dim}${label}${C.reset}`;
        let content = `${pointer}${styledLabel}`;
        if (hintW) {
          // Visible width of pointer+label (pointer is 2, label is plain length).
          const used = pointerW + label.length;
          const gap = Math.max(1, geo.contentW - used - hintW);
          content += `${" ".repeat(gap)}${sel ? C.gray : C.dim}${hint}${C.reset}`;
        }
        return content;
      };

      const writeRow = (line: string) => {
        const row = this.clampVisible(sanitizeHudRow(line), rowCap);
        process.stdout.write(`\r\x1b[K${row}\n`);
      };

      // Box frame rows: top + N items + bottom. Redraw rewrites items + bottom
      // (top stays put) so we move up items.length + 1 from below the bottom.
      const draw = (moveUp: boolean) => {
        if (moveUp) process.stdout.write(`\x1b[${items.length + 1}A`);
        else writeRow(pad + buildBoxTopPlain(geo.boxW, border));
        for (let i = 0; i < items.length; i++) {
          writeRow(pad + buildBoxBody(renderItemContent(i), geo.contentW, border));
        }
        writeRow(pad + buildBoxBottom(geo.boxW, border));
      };
      draw(false);

      // Erase title + full box when the menu closes.
      // title present: blank + title = 2; absent: just the leading blank = 1.
      const titleRows = title ? 2 : 1;
      const boxRows = 1 + items.length + 1; // top + items + bottom
      const erase = () => {
        process.stdout.write("\r");
        const up = titleRows + boxRows;
        if (up > 0) process.stdout.write(`\x1b[${up}A`);
        process.stdout.write("\x1b[J");
      };
      const close = (value: T | undefined) => {
        erase();
        this.endTakeover();
        resolve(value);
      };
      this.takeoverHandler = (str, key) => {
        if (!key) return;
        if (key.name === "up" || str === "k") {
          idx = (idx - 1 + items.length) % items.length;
          draw(true);
        } else if (key.name === "down" || str === "j") {
          idx = (idx + 1) % items.length;
          draw(true);
        } else if (key.name === "return" || key.name === "enter") {
          close(items[idx].value);
        } else if (key.name === "escape") {
          close(undefined);
        }
      };
    });
  }

  /**
   * Free-text prompt (single line). Pauses the main input and reads a line —
   * used where a menu can't, e.g. entering an MCP server command. Enter submits,
   * Escape (or empty submit) cancels with null.
   */
  prompt(question: string, placeholder = ""): Promise<string | null> {
    return new Promise((resolve) => {
      let buf = "";
      this.beginTakeover();
      process.stdout.write("\x1b[?25h"); // we want a visible caret while typing
      process.stdout.write(`\n${C.cyan}┃${C.reset} ${question}\n`);
      if (placeholder) process.stdout.write(`${C.gray}┃ e.g. ${placeholder}${C.reset}\n`);
      const draw = () => {
        process.stdout.write(`\r\x1b[K${C.cyan}┃${C.reset} ${C.bold}›${C.reset} ${buf}`);
      };
      draw();
      const finish = (value: string | null) => {
        process.stdout.write("\n");
        this.endTakeover();
        resolve(value);
      };
      this.takeoverHandler = (str, key) => {
        if (key?.name === "escape") return finish(null);
        if (key?.name === "return" || key?.name === "enter") return finish(buf.trim() || null);
        if (key?.name === "backspace") {
          buf = buf.slice(0, -1);
          draw();
          return;
        }
        // Ignore control keys; accept printable input (including pasted text).
        if (str && !key?.ctrl && !key?.meta && str >= " ") {
          buf += str;
          draw();
        }
      };
    });
  }

  banner(lines: string[]): void {
    this.clearBottom();
    process.stdout.write("\n");
    for (const l of lines) process.stdout.write(l + "\n");
  }
}
